import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TextInput,
  TouchableOpacity,
  FlatList,
  Image,
  Alert,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../convex/_generated/api';

const CommentModal = ({ visible, onClose, moment, privyDid, onCommentAdded }) => {
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textInputRef = useRef(null);

  // Queries
  const commentsData = useQuery(
    api.moments.getCommentsByMoment,
    moment ? { momentId: moment._id } : "skip"
  ) || [];
  
  // Ensure comments are unique by _id
  const comments = commentsData.filter((comment, index, self) => 
    index === self.findIndex(c => c._id === comment._id)
  );

  // Mutations
  const createComment = useMutation(api.moments.createComment);
  const deleteComment = useMutation(api.moments.deleteComment);

  // Auto-focus text input when modal opens
  useEffect(() => {
    if (visible && textInputRef.current) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        textInputRef.current?.focus();
      }, 300);
    }
  }, [visible]);

  const handleSubmitComment = async () => {
    if (!commentText.trim() || !moment || !privyDid) return;

    setIsSubmitting(true);
    try {
      await createComment({
        momentId: moment._id,
        privyDid,
        text: commentText.trim(),
      });
      
      setCommentText('');
      
      // Trigger refresh in parent
      if (onCommentAdded) {
        onCommentAdded();
      }
    } catch (error) {
      console.error('Error creating comment:', error);
      Alert.alert('Error', 'Failed to post comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = (commentId) => {
    Alert.alert(
      'Delete Comment',
      'Are you sure you want to delete this comment?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteComment({ commentId, privyDid });
              if (onCommentAdded) {
                onCommentAdded();
              }
            } catch (error) {
              console.error('Error deleting comment:', error);
              Alert.alert('Error', 'Failed to delete comment. Please try again.');
            }
          },
        },
      ]
    );
  };

  const getRelativeTime = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    
    // Get calendar dates (year, month, day)
    const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const timeDate = new Date(time.getFullYear(), time.getMonth(), time.getDate());
    
    // Calculate the difference in calendar days
    const diffInMs = nowDate.getTime() - timeDate.getTime();
    const daysDiff = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    const years = Math.floor(daysDiff / 365);
    const months = Math.floor(daysDiff / 30);
    const weeks = Math.floor(daysDiff / 7);

    if (years > 0) return `${years}y`;
    if (months > 0) return `${months}m`;
    if (weeks > 0) return `${weeks}w`;
    if (daysDiff > 0) return `${daysDiff}d`;
    if (daysDiff === 0) return 'today';
    return 'now';
  };

  const renderComment = ({ item }) => {
    const isOwnComment = item.user?.privyDid === privyDid;
    
    return (
      <View style={styles.commentItem}>
        <View style={styles.commentHeader}>
          <Image
            source={{ 
              uri: item.user?.avatarUrl || 'https://via.placeholder.com/32'
            }}
            style={styles.commentAvatar}
          />
          <View style={styles.commentInfo}>
            <View style={styles.commentNameRow}>
              <Text style={styles.commentUsername}>
                @{item.user?.username || 'unknown'}
              </Text>
              <Text style={styles.commentTime}>
                {getRelativeTime(item.createdAt)}
              </Text>
            </View>
          </View>
          {isOwnComment && (
            <TouchableOpacity
              style={styles.deleteButton}
              onPress={() => handleDeleteComment(item._id)}
            >
              <Ionicons name="trash-outline" size={16} color="#FF3B30" />
            </TouchableOpacity>
          )}
        </View>
        <Text style={styles.commentText}>{item.text}</Text>
      </View>
    );
  };

  if (!moment) return null;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <KeyboardAvoidingView 
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 50 : 80}
      >
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Ionicons name="close" size={24} color="#1A1A1A" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Comments</Text>
          <View style={styles.placeholder} />
        </View>

        {/* Comments List */}
        <FlatList
          data={comments}
          renderItem={renderComment}
          keyExtractor={(item, index) => `${item._id}-${item.createdAt || index}`}
          style={styles.commentsList}
          contentContainerStyle={styles.commentsContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          contentInsetAdjustmentBehavior="automatic"
          ListEmptyComponent={() => (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>Be the first to comment!</Text>
            </View>
          )}
        />

        {/* Comment Input */}
        <View style={styles.inputContainer}>
          <TextInput
            ref={textInputRef}
            style={[styles.textInput, { color: '#000000' }]}
            placeholder="Add a comment..."
            value={commentText}
            onChangeText={setCommentText}
            multiline
            maxLength={500}
            editable={!isSubmitting}
            returnKeyType="default"
            blurOnSubmit={false}
            textAlignVertical="top"
          />
          <TouchableOpacity
            style={[
              styles.submitButton,
              (!commentText.trim() || isSubmitting) && styles.submitButtonDisabled
            ]}
            onPress={handleSubmitComment}
            disabled={!commentText.trim() || isSubmitting}
          >
            <Ionicons 
              name="send" 
              size={20} 
              color={commentText.trim() && !isSubmitting ? "white" : "#8B8B8B"} 
            />
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F5F3',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
    backgroundColor: 'white',
  },
  closeButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1A1A1A',
  },
  placeholder: {
    width: 40,
  },
  commentsList: {
    flex: 1,
  },
  commentsContent: {
    padding: 16,
    paddingBottom: 20,
  },
  commentItem: {
    marginBottom: 16,
  },
  commentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  commentAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: 12,
  },
  commentInfo: {
    flex: 1,
  },
  commentNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  commentUsername: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1A1A1A',
  },
  commentTime: {
    fontSize: 12,
    color: '#8B8B8B',
    fontWeight: '500',
  },
  deleteButton: {
    padding: 4,
  },
  commentText: {
    fontSize: 14,
    color: '#1A1A1A',
    lineHeight: 20,
    marginLeft: 44,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 60,
    backgroundColor: 'white',
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
  },
  textInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 10,
    marginRight: 12,
    minHeight: 40,
    maxHeight: 100,
    fontSize: 16,
    backgroundColor: 'white',
    color: '#000000',
  },
  submitButton: {
    backgroundColor: '#522504',
    borderRadius: 20,
    padding: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  submitButtonDisabled: {
    backgroundColor: '#E0E0E0',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyStateText: {
    fontSize: 16,
    color: '#8B8B8B',
    fontWeight: '500',
    textAlign: 'center',
  },
});

export default CommentModal;
